import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import apiService from "../../service/apiService";
import { FaRegEdit, FaEye, FaTrash } from "react-icons/fa";
import { Pagination } from "react-bootstrap";
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Download } from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import { useCallback } from "react";
import { debounce } from "lodash";

const Technologypark = () => {
  const navigate = useNavigate();
  
  // Essential state variables only
  const [open, setOpen] = useState(false);
  const [pageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [districtName, setDistrictName] = useState("");
  const [blockName, setBlockName] = useState("");
  const [date, setDate] = useState("");
  const [location, setLocation] = useState("");
  const [designation, setDesignation] = useState("");
  const [totalItems, setTotalitems] = useState(0);
  const [totalpage, setTotalpage] = useState(0);
  const [resourcesName, setResourcesName] = useState('');
  const [technologyParkId, setTechnologyParkId] = useState(null);
  const [seasonName, setSeasonName] = useState("");
  const [mipName, setMipName] = useState('');
  const [seedName, setSeedName] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [seasons, setSeasons] = useState([]);
  const [years, setYears] = useState([]);
  const [errors, setErrors] = useState({});
  
  const loginData = JSON.parse(localStorage.getItem("loginData"));
  const [dist, setDist] = useState(loginData?.districtId ? loginData.districtId : 0);
  const [blk, setBlk] = useState(loginData?.blockId ? loginData.blockId : 0);
  
  const [formData, setFormData] = useState({
    date: "",
    seasonId: "",
    location: "",
    designation: "",
    resourcesName: "",
  });

  // Essential useEffects
  const fetchSeasons = async () => {
    try {
      const response = await apiService.get("/seasons/get-all");
      setSeasons(response.data);
    } catch (error) {
      console.error("Error fetching seasons:", error);
    }
  };

  const fetchYears = async () => {
    try {
      const response = await apiService.get("/years/get-all");
      setYears(response.data);
      const currentYear = new Date().getFullYear();
      const defaultYear = response.data.find((yr) => yr.yearValue === currentYear);
      setYear(defaultYear ? defaultYear.yearValue : response.data[0].yearValue);
    } catch (error) {
      console.error("Error fetching years:", error);
    }
  };

  const fetchAllDist = async () => {
    try {
      const response = await apiService.get('/districts/get-all');
      const currentDist = loginData?.districtId ? loginData.districtId : 0;
      if (currentDist) {
        const defDist = response.data.find((dis) => dis.districtId === currentDist);
        setDistrictName(defDist ? defDist.districtName : "");
      }
    } catch (error) {
      setDistrictName("");
      console.error('Error fetching districts:', error);
    }
  };

  const fetchAllBlocks = async () => {
    try {
      const response = await apiService.get(`/master/get-block-by-district/${dist}`);
      const currentBlock = loginData?.blockId ? loginData.blockId : 0;
      if (currentBlock) {
        const defBlock = response.data.body.blocks.find((blk) => blk.blockId === currentBlock);
        setBlockName(defBlock ? defBlock.blockName : "");
      }
    } catch (error) {
      setBlockName("");
      console.error("Error fetching blocks:", error);
    }
  }

  useEffect(() => {
    if (dist > 0) {
      fetchAllBlocks();
    }
  }, [dist]);

  useEffect(() => {
    fetchSeasons();
    fetchYears();
    fetchAllDist();
  }, []);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const fetchData = async () => {
    try {
      const payload = {
        technologyParkId: 0,
        seasonName: seasonName,
        location: location,
        date: date ? format(parseISO(date), "dd-MM-yyyy") : "",
        seedName: seedName,
        climateSmartCharacteristics: "",
        resourcesName: resourcesName,
        designation: designation,
        mipName: mipName,
        blockName: blockName,
        districtName: districtName,
        pageNumber: page,
        pageSize: 10,
        year: year,
      };

      const response = await apiService.post("/technology/getall", payload);

      if (response.data.status) {
        setTableData(response.data.body.data);
        setTotalitems(response.data.body.recordsCount);
        setTotalpage(response.data.body.pageCount);
      } else {
        setTableData([]);
        setTotalitems(0);
        setTotalpage(0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setTableData([]);
      setTotalitems(0);
      setTotalpage(0);
    }
  };

  const debouncedFetchData = useCallback(debounce(fetchData, 500), [
    page,
    pageSize,
    seasonName,
    seedName,
    resourcesName,
    designation,
    mipName,
    location,
    blockName,
    districtName,
    date,
    year,
  ]);

  useEffect(() => {
    debouncedFetchData();
    return () => debouncedFetchData.cancel();
  }, [debouncedFetchData]);

  const handleView = (item) => {
    navigate("/childtechnologypark", { state: { technologyParkId: item.technologyParkId } });
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({});
  };

  const handleOpenEdit = async (item) => {
    try {
      const response = await apiService.get(`/technology/details/${item.technologyParkId}`);
      setTechnologyParkId(item.technologyParkId);
      const data = response.data;
      setFormData({
        date: data.date || "",
        seasonId: data.seasonId || "",
        location: data.location || "",
        designation: data.designation || "",
        resourcesName: data.resourcesName || "",
      });
      setOpen(true);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleSave = () => {
    const payload = {
      Date: formData.date,
      SeasonId: formData.seasonId,
      Location: formData.location,
      Designation: formData.designation,
      ResourcesName: formData.resourcesName,
    };

    apiService
      .put(`/technology/generalInfo/${technologyParkId}`, payload)
      .then((response) => {
        console.log("Data saved successfully:", response.data);
        handleClose();
        fetchData();
      })
      .catch((error) => {
        console.error("Error saving data:", error.response || error.message);
      });
  };

  const handleDelete = async (item) => {
    const confirmDelete = window.confirm(`Are you sure you want to delete item ?`);
    if (!confirmDelete) return;

    try {
      const response = await apiService.delete(`/technology/delete/${item.technologyParkId}`);
      if (response.status === 200) {
        alert("Item deleted successfully!");
        setTableData((prevData) => prevData.filter(row => row.technologyParkId !== item.technologyParkId));
      } else {
        alert("Failed to delete the item. Please try again.");
      }
    } catch (error) {
      console.error("Error deleting the item:", error);
      alert("An error occurred while deleting the item.");
    }
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
  };

  const renderPaginationItems = () => {
    const paginationItems = [];
    const maxPagesToShow = 7;
    const startPages = 3;
    const endPages = 2;

    if (totalpage <= maxPagesToShow) {
      for (let pages = 1; pages <= totalpage; pages++) {
        paginationItems.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }
    } else {
      for (let pages = 1; pages <= startPages; pages++) {
        paginationItems.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }

      if (page > startPages + 1) {
        paginationItems.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }

      const middlePages = [];
      const startMiddlePage = Math.max(page - 1, startPages + 1);
      const endMiddlePage = Math.min(page + 1, totalpage - endPages);

      for (let pages = startMiddlePage; pages <= endMiddlePage; pages++) {
        middlePages.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }

      paginationItems.push(...middlePages);

      if (page < totalpage - endPages - 1) {
        paginationItems.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }

      for (let pages = totalpage - endPages + 1; pages <= totalpage; pages++) {
        paginationItems.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }
    }

    return paginationItems;
  };

  const handleDownloadExcel = () => {
    const headers = [
      "Season", "District", "Block", "MIP", "Location", "Crop", "Varieties(no.)",
      "Sowing Date", "Visitor(no.)", "Resources Name", "Designation",
    ];
    const worksheetData = [headers];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "sheet1");
    XLSX.writeFile(workbook, "Technology_park_format.xlsx");
  };

  const exportToExcel = async () => {
    const headers = [
      "Season", "Year", "District", "Block", "MIP", "Location", "Crop", "Varieties",
      "Sowing Date", "PG Names", "Farmer(no.)", "Visitor(no.)", "Resources Name", "Designation",
    ];

    try {
      const payload = {
        technologyParkId: 0,
        seasonName: seasonName || "",
        location: location || "",
        date: date ? format(parseISO(date), "dd-MM-yyyy") : "",
        seedName: seedName || "",
        climateSmartCharacteristics: "",
        resourcesName: resourcesName || "",
        designation: designation || "",
        mipName: mipName || "",
        blockName: blockName || "",
        districtName: districtName || "",
        pageNumber: page,
        pageSize: 10000000,
        year: year || "",
      };

      const response = await apiService.post("/technology/getall", payload);

      if (response?.data?.body?.data) {
        const data = response.data.body.data;
        const formattedData = data.map((item, index) => [
          index + 1,
          item.yearValue || "",
          item.seasonName || "",
          item.districtName || "",
          item.blockName || "",
          item.mips || "",
          item.location || "",
          item.seedName || 0,
          Array.isArray(item.seedVarieties) && item.seedVarieties.length > 0 ? item.seedVarieties.map(v => v.seedVarietyName).join(", ") : "",
          item.date ? format(parseISO(item.date), "dd-MM-yyyy") : "",
          item.pgName || "",
          item.farmers ? item.farmers.split(', ').length : 0,
          item.visitors || 0,
          item.resourcesName || "",
          item.designation || "",
        ]);

        const worksheetData = [headers, ...formattedData];
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
        XLSX.writeFile(workbook, `Technology_park_${year}.xlsx`);
      }
    } catch (error) {
      console.error("Error exporting data to Excel:", error);
    }
  };

  return (
    <Box style={{
      backgroundColor: "#f8f9fa",
      overflowY: "auto"
    }}>
      {/* Header - Same design as ffd.jsx */}
      <div style={{
        backgroundColor: "#ffffff",
        borderBottom: "3px solid #007bff",
        boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
      }}>
        <div className="container-fluid">
          <div className="d-flex justify-content-between align-items-center py-3 px-4">
            <div className="d-flex align-items-center">
              <div>
                <h1 style={{
                  fontSize: "1.8rem",
                  fontWeight: "600",
                  color: "#2c3e50",
                  margin: "0"
                }}>
                  Technology Park Management
                </h1>
              </div>
            </div>

            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center">
                <label style={{
                  fontWeight: "500",
                  color: "#495057",
                  marginRight: "10px",
                  fontSize: "14px"
                }}>
                  Year:
                </label>
                <select
                  className="form-select"
                  value={year || 0}
                  onChange={(e) => setYear(Number(e.target.value))}
                  style={{
                    border: "1px solid #ced4da",
                    borderRadius: "6px",
                    padding: "6px 12px",
                    fontSize: "14px",
                    minWidth: "100px",
                    backgroundColor: "#fff"
                  }}
                >
                  <option value={0} disabled>Select Year</option>
                  {years.map((yr) => (
                    <option key={yr.yearId} value={yr.yearValue}>
                      {yr.yearValue}
                    </option>
                  ))}
                </select>
              </div>

              <Button
                variant="contained"
                onClick={() => navigate("/addtechnologypark")}
                style={{
                  backgroundColor: "#28a745",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                  textTransform: "none"
                }}
              >
                + Add New
              </Button>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={exportToExcel}
                style={{
                  backgroundColor: "#007bff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                  textTransform: "none"
                }}
              >
                Export Excel
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Same design as ffd.jsx */}
      <div className="container-fluid" style={{ padding: "20px" }}>
        <div style={{
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 160px)"
        }}>
          <div style={{
            flex: "1",
            overflowY: "auto",
            overflowX: "auto"
          }}>
            <div className="table-responsive">
              <table className="table table-hover mb-0" style={{
                minWidth: "1800px",
                backgroundColor: "#fff"
              }}>
                <thead style={{
                  backgroundColor: "#007bff",
                  color: "white",
                  position: "sticky",
                  top: "0",
                  zIndex: "10"
                }}>
                  <tr>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      textAlign: "center",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "60px"
                    }}>
                      SL
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      textAlign: "center",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "60px"
                    }}>
                      Year
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "120px"
                    }}>
                      Season
                      <br />
                      <select
                        className="form-control form-control-sm mt-2"
                        value={seasonName}
                        onChange={(e) => { setSeasonName(e.target.value); setPage(1); }}
                        style={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #ced4da",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: "#333",
                          padding: "4px 8px"
                        }}
                      >
                        <option value="">All</option>
                        {seasons.map((s) => (
                          <option key={s.seasonId} value={s.seasonName}>
                            {s.seasonName}
                          </option>
                        ))}
                      </select>
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "140px"
                    }}>
                      District
                      <br />
                      <input
                        type="text"
                        placeholder="Search"
                        className="form-control form-control-sm mt-2"
                        value={districtName}
                        onChange={(e) => {
                          if (dist === 0) {
                            setDistrictName(e.target.value);
                            setPage(1);
                          }
                        }}
                        disabled={dist > 0}
                        style={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #ced4da",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: "#333",
                          padding: "4px 8px"
                        }}
                      />
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "120px"
                    }}>
                      Block
                      <br />
                      <input
                        type="text"
                        placeholder="Search"
                        className="form-control form-control-sm mt-2"
                        value={blockName}
                        onChange={(e) => {
                          if (blk === 0) {
                            setBlockName(e.target.value);
                            setPage(1);
                          }
                        }}
                        disabled={blk > 0}
                        style={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #ced4da",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: "#333",
                          padding: "4px 8px"
                        }}
                      />
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "150px"
                    }}>
                      MIP
                      <br />
                      <input
                        type="text"
                        placeholder="Search"
                        className="form-control form-control-sm mt-2"
                        value={mipName}
                        onChange={(e) => { setMipName(e.target.value); setPage(1); }}
                        style={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #ced4da",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: "#333",
                          padding: "4px 8px"
                        }}
                      />
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "120px"
                    }}>
                      Location
                      <br />
                      <input
                        type="text"
                        placeholder="Search"
                        className="form-control form-control-sm mt-2"
                        value={location}
                        onChange={(e) => { setLocation(e.target.value); setPage(1); }}
                        style={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #ced4da",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: "#333",
                          padding: "4px 8px"
                        }}
                      />
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "100px",
                      textAlign: "center"
                    }}>
                      Crop
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "100px",
                      textAlign: "center"
                    }}>
                      Varieties
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "120px"
                    }}>
                      Sowing Date
                      <br />
                      <input
                        type="date"
                        className="form-control form-control-sm mt-2"
                        value={date}
                        onChange={(e) => { setDate(e.target.value); setPage(1); }}
                        style={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #ced4da",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: "#333",
                          padding: "4px 8px"
                        }}
                      />
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "100px",
                      textAlign: "center"
                    }}>
                      PG Names
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "100px",
                      textAlign: "center"
                    }}>
                      Farmer(no.)
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "100px",
                      textAlign: "center"
                    }}>
                      Visitor(no.)
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "150px"
                    }}>
                      Resources Person Name
                      <br />
                      <input
                        type="text"
                        placeholder="Search"
                        className="form-control form-control-sm mt-2"
                        value={resourcesName}
                        onChange={(e) => { setResourcesName(e.target.value); setPage(1); }}
                        style={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #ced4da",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: "#333",
                          padding: "4px 8px"
                        }}
                      />
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "120px"
                    }}>
                      Designation
                      <br />
                      <input
                        type="text"
                        placeholder="Search"
                        className="form-control form-control-sm mt-2"
                        value={designation}
                        onChange={(e) => { setDesignation(e.target.value); setPage(1); }}
                        style={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #ced4da",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: "#333",
                          padding: "4px 8px"
                        }}
                      />
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      textAlign: "center",
                      minWidth: "120px"
                    }}>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: "13px" }}>
                  {tableData.map((item, index) => (
                    <tr key={index} style={{
                      backgroundColor: index % 2 === 0 ? "#f8f9fa" : "#ffffff",
                      borderBottom: "1px solid #dee2e6"
                    }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#e3f2fd";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#f8f9fa" : "#ffffff";
                      }}>
                      <td style={{
                        padding: "12px 8px",
                        textAlign: "center",
                        fontWeight: "600",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6"
                      }}>
                        {(page - 1) * 10 + index + 1}
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        textAlign: "center",
                        fontWeight: "600",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6"
                      }}>
                        {item.yearValue}
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6"
                      }}>
                        <span style={{
                          backgroundColor: item.seasonName === 'Rabi' ? '#d4edda' : '#fff3cd',
                          color: item.seasonName === 'Rabi' ? '#155724' : '#856404',
                          padding: "3px 8px",
                          borderRadius: "4px",
                          fontSize: "11px",
                          fontWeight: "500"
                        }}>
                          {item.seasonName}
                        </span>
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6",
                        fontWeight: "500"
                      }}>
                        {item.districtName}
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6"
                      }}>
                        {item.blockName}
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6",
                        fontSize: "12px"
                      }}>
                        {item.mips || "NA"}
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6"
                      }}>
                        {item.location}
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        textAlign: "center",
                        fontWeight: "600",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6"
                      }}>
                          {item.seedName || ""}
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        textAlign: "center",
                        fontWeight: "600",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6"
                      }}>
                          {Array.isArray(item.seedVarieties) && item.seedVarieties.length > 0
                          ? item.seedVarieties.map(v => v.seedVarietyName).join(", ")
                          : "NA"
                        }
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6",
                        fontSize: "12px"
                      }}>
                        {item.date ? format(parseISO(item.date), "dd-MM-yyyy") : "NA"}
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        textAlign: "center",
                        fontWeight: "600",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6"
                      }}>
                          {item.pgName || ""}
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        textAlign: "center",
                        fontWeight: "600",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6"
                      }}>
                        <span style={{
                          backgroundColor: '#ffc107',
                          color: '#212529',
                          padding: "3px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "600"
                        }}>
                          {item.farmers ? item.farmers.split(', ').length : 0}
                        </span>
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        textAlign: "center",
                        fontWeight: "600",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6"
                      }}>
                        <span style={{
                          backgroundColor: '#ffc107',
                          color: '#212529',
                          padding: "3px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "600"
                        }}>
                          {item.visitors || 0}
                        </span>
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6"
                      }}>
                        {item.resourcesName || "NA"}
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6"
                      }}>
                        {item.designation || "NA"}
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        textAlign: "center"
                      }}>
                        <div className="d-flex justify-content-center gap-2">
                          <button
                            className="btn btn-sm"
                            onClick={() => handleView(item)}
                            style={{
                              backgroundColor: "#007bff",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              padding: "4px 8px",
                              fontSize: "12px"
                            }}
                            title="View Details"
                          >
                            <FaEye />
                          </button>
                          <button
                            className="btn btn-sm"
                            onClick={() => handleOpenEdit(item)}
                            style={{
                              backgroundColor: "#28a745",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              padding: "4px 8px",
                              fontSize: "12px"
                            }}
                            title="Edit"
                          >
                            <FaRegEdit />
                          </button>
                          <button
                            className="btn btn-sm"
                            onClick={() => handleDelete(item)}
                            style={{
                              backgroundColor: "#dc3545",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              padding: "4px 8px",
                              fontSize: "12px"
                            }}
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div style={{
            borderTop: "1px solid #dee2e6",
            padding: "15px 20px",
            backgroundColor: "#f8f9fa",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <div style={{
              fontSize: "14px",
              color: "#6c757d"
            }}>
              Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalItems)} of {totalItems} entries
            </div>
            <Pagination size="sm">
              <Pagination.First onClick={() => handlePageChange(1)} disabled={page === 1} />
              <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 1} />
              {renderPaginationItems()}
              <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page === totalpage} />
              <Pagination.Last onClick={() => handlePageChange(totalpage)} disabled={page === totalpage} />
            </Pagination>
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle style={{
          backgroundColor: "#007bff",
          color: "white",
          fontWeight: "600"
        }}>
          Edit Technology Park
        </DialogTitle>
        <DialogContent style={{ padding: "20px" }}>
          <div className="row g-3 mt-1">
            <div className="col-md-6">
              <TextField
                fullWidth
                label="Date"
                type="date"
                name="date"
                value={formData.date || ""}
                onChange={handleFormChange}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </div>
            <div className="col-md-6">
              <FormControl fullWidth size="small">
                <InputLabel>Season</InputLabel>
                <Select
                  name="seasonId"
                  value={formData.seasonId || ""}
                  onChange={handleFormChange}
                  label="Season"
                >
                  {seasons.map((season) => (
                    <MenuItem key={season.seasonId} value={season.seasonId}>
                      {season.seasonName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="col-md-6">
              <TextField
                fullWidth
                label="Location"
                name="location"
                value={formData.location || ""}
                onChange={handleFormChange}
                size="small"
              />
            </div>
            <div className="col-md-6">
              <TextField
                fullWidth
                label="Designation"
                name="designation"
                value={formData.designation || ""}
                onChange={handleFormChange}
                size="small"
              />
            </div>
            <div className="col-md-12">
              <TextField
                fullWidth
                label="Resources Name"
                name="resourcesName"
                value={formData.resourcesName || ""}
                onChange={handleFormChange}
                size="small"
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions style={{ padding: "15px 20px" }}>
          <Button
            onClick={handleClose}
            style={{
              color: "#6c757d",
              textTransform: "none"
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            style={{
              backgroundColor: "#007bff",
              textTransform: "none"
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Technologypark;

